<script lang="ts">
	import { Models as M } from "@lux-ai/api-interface"
	import { getProgressController } from "../../ProgressHandler.svelte"
	import { ProgressBar } from "misc-ui"
	import { getLuxAiController } from "../../LuxAIController.svelte"
	import { closeOverlay, openConfirmOverlay } from "overlay"
	import { ErrorReportIcon } from "luxedo-comps"
	import { cancelGeneration } from "../../../modules/operations"

	interface Props {
		theme: M.Theme
		generation: M.ThemeGeneration
	}

	let { theme, generation }: Props = $props()
	let progressController = getProgressController()
	let progress = $derived(progressController?.progress[generation.id])
	let errorMessage = $derived(progressController?.errors[generation.id])

	let isCancelling = $state(false)

	const controller = getLuxAiController()

	function verifyCancel() {
		const overlayId = openConfirmOverlay({
			buttons: {
				confirm: {
					text: "Yes, cancel",
					onClick: async () => {
						isCancelling = true
						await cancelGeneration(generation.id, controller)
					},
				},
				deny: {
					text: "No",
					onClick: () => closeOverlay(overlayId),
				},
			},
			prompt: [`Are you sure you want to cancel generating ${theme.name}? `],
		})
	}

	async function viewExistingScene() {
		controller.refreshActiveGenerations()
		controller.quickActions.viewScene(generation.scene_id)
	}
</script>

<div class="gen-preview">
	<div class="top-row">
		<div class="theme-preview">
			{#if errorMessage}
				<ErrorReportIcon />
			{:else}
				<img src={theme.thumbnailSrc} alt="" />
			{/if}
		</div>
		<div class="progress-container">
			<h3>{theme.name}</h3>
			{#if errorMessage}
				<span class="error-message">
					{errorMessage}
				</span>
			{:else}
				<ProgressBar percentage={progress} width={"100%"} />
			{/if}
		</div>
	</div>
	<div class="button-container">
		{#if !generation.scene_id}
			<button
				class="square-button outline-button alt"
				onclick={() => controller.viewGeneration(generation)}>View</button
			>
			<button
				class="square-button outline-button warn"
				onclick={() => verifyCancel()}
				disabled={isCancelling}>Cancel</button
			>
		{:else}
			<button class="square-button outline-button" onclick={viewExistingScene}>View Scene</button>
		{/if}
	</div>
</div>

<style>
	.gen-preview {
		/* min-height: 5rem; */
		padding: 1rem;
		border-radius: var(--br);
		background-color: var(--color-bg);
		gap: 1rem;
		display: flex;
		flex-direction: row;
	}

	.theme-preview {
		position: relative;
		aspect-ratio: 4/3;
	}

	.top-row {
		height: 3rem;
		display: flex;
		flex-direction: row;
		flex-grow: 1;
		gap: 1rem;
	}

	.error-message {
		color: var(--color-error);
	}

	:global(.mobile) .gen-preview {
		flex-direction: column;
	}

	.theme-preview img {
		position: absolute;
		object-fit: cover;
		width: 100%;
		height: 100%;
		border-radius: var(--br);
	}

	.theme-preview :global(svg) {
		max-height: 100%;
		width: auto;
	}

	.progress-container {
		display: flex;
		flex-direction: column;
		justify-content: space-evenly;
		flex-grow: 1;

		line-height: 1em;
	}

	.progress-container h3 {
		font-family: var(--font-heading);
		margin: 0;
		font-size: var(--h1);
	}

	:global(.mobile) .progress-container h3 {
		font-size: var(--h2);
	}

	.progress-container :global(.progress-bar) {
		margin: 0;
	}

	.button-container {
		width: fit-content;
		margin: 0;
	}

	button {
		cursor: pointer;
	}

	:global(.mobile) .button-container {
		width: 100%;
	}

	:global(.mobile) button {
		flex-grow: 1;
	}
</style>
